import { createSlice } from "@reduxjs/toolkit";
import { USER_DETAIL, JWT_TOKEN, REFRESH_TOKEN, COMMON_CONTENT, LOGIN_TYPE, SPA_LOGIN_FLOW, FCM_TOKEN, ACTIVE_SPA_HOME, CUSTOMIZING_DETAIL, BOOK_MASSAGE, GIFT_DETAIL, COMMON_KEY } from "../types";

const INITIAL_STATE = {
    jwtToken: null,
    commonKey: null,
    fcmToken: null,
    userDetail: null,
    loginType: 'user',
    refreshToken: null,
    commonData: null,
    spaLoginFlow: null,
    activeSpaHome: 1,
    bookMassage: {},
    customizing: null,
    giftDetail: {}
};

/**
 * The `common` slice.
 *
 * Converted from a hand-written switch reducer to createSlice: `legacy_createStore` and
 * the manual reducer were deprecated, and this brings Immer plus the standard RTK setup.
 *
 * ⚠️ The payload shapes below are INTENTIONALLY preserved, quirks and all, because ~100
 * dispatch sites depend on them:
 *
 *   - Action creators dispatch `storeX({ type: X, payload: value })`, so `action.payload`
 *     is itself `{ type, payload }`.
 *   - Some cases unwrap that (`payload?.payload`) and store the bare value:
 *     commonKey, giftDetail, bookMassage, loginType, userDetail.
 *   - Others store the whole wrapper: jwtToken, fcmToken, commonData, spaLoginFlow,
 *     activeSpaHome, customizing, refreshToken. Readers of those must use `?.payload`,
 *     which is why src/Redux/selectors.js is split the same way.
 *   - USER_DETAIL additionally IGNORES a falsy payload rather than clearing, so signing
 *     out requires `{ payload: null }` (a bare `null` is a no-op). forceLogout relies on
 *     this; reducer/common.test.js documents it.
 *
 * Normalising these is worth doing, but it is a separate change: the selectors now
 * insulate every read site, so the shapes can be unified without touching consumers.
 * `extraReducers` is used rather than named slice actions so the existing string
 * constants in ../types keep working unchanged.
 */
const commonSlice = createSlice({
    name: 'common',
    initialState: INITIAL_STATE,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // --- stored as the whole { type, payload } wrapper ---
            .addCase(JWT_TOKEN, (state, action) => {
                state.jwtToken = action.payload;
            })
            .addCase(FCM_TOKEN, (state, action) => {
                state.fcmToken = action.payload;
            })
            .addCase(COMMON_CONTENT, (state, action) => {
                state.commonData = action.payload;
            })
            .addCase(SPA_LOGIN_FLOW, (state, action) => {
                state.spaLoginFlow = action.payload;
            })
            .addCase(ACTIVE_SPA_HOME, (state, action) => {
                state.activeSpaHome = action.payload;
            })
            .addCase(REFRESH_TOKEN, (state, action) => {
                state.refreshToken = action.payload;
            })
            .addCase(CUSTOMIZING_DETAIL, (state, action) => {
                state.customizing = action.payload;
            })

            // --- unwrapped to the bare value ---
            .addCase(COMMON_KEY, (state, action) => {
                state.commonKey = action.payload?.payload;
            })
            .addCase(GIFT_DETAIL, (state, action) => {
                state.giftDetail = action.payload?.payload;
            })
            .addCase(LOGIN_TYPE, (state, action) => {
                state.loginType = action.payload?.payload;
            })

            // A null payload clears the in-progress booking; anything else merges into it.
            .addCase(BOOK_MASSAGE, (state, action) => {
                const incoming = action.payload?.payload;
                state.bookMassage =
                    incoming == null ? incoming : { ...state.bookMassage, ...incoming };
            })

            // Falsy payload is ignored, not cleared — see the note above.
            .addCase(USER_DETAIL, (state, action) => {
                if (action.payload) {
                    state.userDetail = action.payload?.payload;
                }
            });
    },
});

export default commonSlice.reducer;
